import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { trans } from '@spotahome/soyuz/client';
import {
  ExposedFilters,
  useFiltersContext,
  Chip,
  FilterUtils,
  CHANNELS,
  useFacetSearchContext
} from '@spotahome/ui-library';

import { Mediator } from '@spotahome/soyuz-mediator';

import { SEO_FILTERS_CLOSE, SEO_FILTERS_OPEN } from '../common/utils/channels';

import BILLS from '../components/Filters/bills-config';
import './SearchFilters.scss';

const BILLS_NAMES = BILLS.map(({ name }) => name);

const SearchFilters = ({ city }) => {
  const { filters, handleUpdateFilters } = useFiltersContext();
  const { facetSearch, setFilters: setFacetFilters } = useFacetSearchContext();
  const [filtersCount, setFiltersCount] = useState(0);
  const filtersWrapper = useRef(null);

  const omitSoldOut = true; // avoid boolean flag smell

  useEffect(() => {
    const filtersWithoutExposedFilters = {
      ...filters,
      'move-in': undefined,
      'move-out': undefined,
      moveOutFrom: undefined,
      moveOutTo: undefined,
      moveInFrom: undefined,
      moveInTo: undefined,
      budget: undefined,
      'rentalType[]': undefined,
      'type[]': undefined,
      'topology[]': undefined,
      'features[]': filters['features[]'].filter(
        feature => !BILLS_NAMES.includes(feature)
      )
    };

    setFiltersCount(
      FilterUtils.getActiveFiltersCount(filtersWithoutExposedFilters)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFiltersUpdate = newFilters => {
    const filtersToApply = {
      ...filters,
      ...newFilters
    };
    handleUpdateFilters(filtersToApply, omitSoldOut);
    setFacetFilters(filtersToApply);
  };

  const handleFiltersOpen = filterName => {
    // Old event keep triggering
    Mediator.publish(SEO_FILTERS_CLOSE);
    SoyuzAnalytics.sendGA4Event('exposed-filters-open', { method: filterName });
  };

  const handleFiltersClose = filterName => {
    // Old event keep triggering
    SoyuzAnalytics.sendGA4Event('exposed-filters-close', {
      method: filterName
    });
  };

  const handleFullFilterOpen = e => {
    SoyuzAnalytics.sendGA4Event('open-exposed-filters', {});
    e?.stopPropagation();
    filtersWrapper.current.scrollIntoView();
    filtersWrapper.current.classList.add('search-seo-filters--left');
    Mediator.publish(SEO_FILTERS_OPEN);
    Mediator.publish(CHANNELS.CLOSE_EXPOSED_FILTER);
  };

  useEffect(() => {
    const onFiltersClose = () => {
      filtersWrapper.current.classList.remove('search-seo-filters--left');
    };

    Mediator.subscribe(SEO_FILTERS_CLOSE, onFiltersClose);

    return () => {
      Mediator.unsubscribe(SEO_FILTERS_CLOSE, onFiltersClose);
    };
  }, []);

  return (
    <div className="search-seo-filters" ref={filtersWrapper}>
      <div className="search-seo-filters__filters">
        <ExposedFilters
          filters={filters}
          cityId={city.id}
          currencyIsoCode={city.currencyIsoCode}
          totalResults={facetSearch.totalCount}
          onFiltersUpdate={handleFiltersUpdate}
          onFiltersOpen={handleFiltersOpen}
          onFiltersClose={handleFiltersClose}
          maxPriceInCity={city.maximumPrice}
          minimumStaying={city.minimumStaying}
        />
        <Chip.Filter
          label={trans('search.filter_header.more_filters')}
          className="search-seo-filters__open-filters"
          onClick={handleFullFilterOpen}
          dataTest="more-filters-button"
          isSelected={!!filtersCount}
          renderTrailingIcon={() =>
            filtersCount ? <span>{filtersCount}</span> : null
          }
        />
      </div>
    </div>
  );
};

SearchFilters.propTypes = {
  city: PropTypes.shape({
    id: PropTypes.string,
    currencyIsoCode: PropTypes.string,
    maximumPrice: PropTypes.number,
    minimumStaying: PropTypes.number,
    cityName: PropTypes.string
  }).isRequired,
  initialFilters: PropTypes.shape({})
};

export default SearchFilters;
